import React, { useId, useState } from "react";
import { Info } from "lucide-react";
import { fieldLabelClasses } from "../../../components/ui";
import {
  PLAN_STRUCTURES,
  PLAN_STRUCTURE_COPY,
  type PlanStructure,
} from "../planStructure";

export interface PlanStructureChoiceProps {
  value: PlanStructure;
  onChange: (structure: PlanStructure) => void;
  /** Turns both halves off while a create is in flight. */
  disabled?: boolean;
}

/**
 * The two ways a meal plan can be laid out, as the pair of buttons at the top
 * of Create new plan: a meal holding several options, or a meal holding the
 * days of a week.
 *
 * First in the dialog, above the name and the kind of plan, because it is the
 * one answer that cannot be changed afterwards — both of those are editable
 * from the builder, while a meal laid out in days is not something any screen
 * turns back into a meal of options. It is also what the rest of the dialog is
 * for: the plan being named is a plan of one shape or the other.
 *
 * A radiogroup rather than two buttons: exactly one is true at a time. Two
 * words each and nothing else, so the choice reads at a glance — what the two
 * actually mean is a sentence each, and a sentence each under a control this
 * small is a wall of grey nobody reads. Both sentences are on "Which one
 * should I choose?" instead, where somebody who does not know can ask for them.
 */
const PlanStructureChoice: React.FC<PlanStructureChoiceProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [explaining, setExplaining] = useState(false);
  // Named on the trigger with `aria-describedby`, so a screen reader reads the
  // explanation as part of the thing it explains rather than as a stray box.
  const tipId = useId();

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <span className={fieldLabelClasses}>Plan structure</span>

        {/* The descriptions, on the pointer rather than always on. Still a
            button, and not only a hover: hovering is nothing on a touchscreen
            and nothing on a keyboard, so a tap opens it and so does tabbing to
            it. Escape closes it the way it closes anything else floating. */}
        <div className="relative">
          <button
            type="button"
            aria-describedby={explaining ? tipId : undefined}
            onMouseEnter={() => setExplaining(true)}
            onMouseLeave={() => setExplaining(false)}
            onFocus={() => setExplaining(true)}
            onBlur={() => setExplaining(false)}
            onClick={() => setExplaining((open) => !open)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setExplaining(false);
            }}
            className="flex items-center gap-1 rounded text-[12px] 3xl:text-[13px] text-ink-subtle transition-colors hover:text-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <Info aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
            Which one should I choose?
          </button>

          {/* Hung under the trigger and pinned to its right edge, so it opens
              into the dialog rather than off it. Below rather than above
              because this sits at the top of the dialog, which is the only
              direction with room. It takes no pointer events: a box that could
              be hovered would flicker on its own edge as the pointer crossed
              it. */}
          {explaining && (
            <dl
              id={tipId}
              role="tooltip"
              className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 w-[278px] rounded-md border border-line bg-white px-3 py-2.5 text-left shadow-[0_8px_24px_rgba(11,27,31,0.12)]"
            >
              {PLAN_STRUCTURES.map((structure) => (
                <div key={structure} className="py-1">
                  <dt className="text-[13px] font-medium text-ink">
                    {PLAN_STRUCTURE_COPY[structure].title}
                  </dt>
                  <dd className="mt-0.5 text-[12px] leading-snug text-ink-muted">
                    {PLAN_STRUCTURE_COPY[structure].description}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>

      <div
        role="radiogroup"
        aria-label="Plan structure"
        className="grid grid-cols-2 gap-3"
      >
        {PLAN_STRUCTURES.map((structure) => {
          const selected = structure === value;

          return (
            <button
              key={structure}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(structure)}
              className={`rounded-[6px] border px-4 py-2.5 text-center text-[14px] 3xl:text-[15px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-60 ${
                selected
                  ? "border-[#5BD3E1] bg-[#EFFBFD] font-medium text-[#00424B]"
                  : "border-line bg-white text-ink enabled:hover:border-[#5BD3E1]"
              }`}
            >
              {PLAN_STRUCTURE_COPY[structure].title}
            </button>
          );
        })}
      </div>

    </div>
  );
};

export default PlanStructureChoice;
